import { useRef, useState } from 'react';
import { ChatView } from './ChatView';
import { mimeFor } from '../lib/archive';
import type { Preview } from '../lib/preview';

/**
 * The whole product, before anyone has an account.
 *
 * A person drops in their own export and reads it immediately. Nothing has
 * been uploaded, no key exists, no request has been made — which makes the
 * privacy claim on this screen the one place it is unarguably true. Signing in
 * is asked for at the only moment it buys anything: keeping it.
 */
export function PreviewView({ preview, onKeep, onPasskey, onDiscard, busy }: {
  preview: Preview;
  onKeep: () => void;
  /** Offered when this browser can do WebAuthn — see the keep bar below. */
  onPasskey?: () => void;
  onDiscard: () => void;
  busy: boolean;
}) {
  return (
    <div className="app preview reading">
      <main>
        <header className="chat-bar">
          <button className="back" onClick={onDiscard}>
            <span aria-hidden="true">‹</span> Close
          </button>
          <strong>{preview.title}</strong>
          <span className="count">{preview.messages.length.toLocaleString()} messages</span>
        </header>
        <MediaReport report={preview.report} />
        <ChatView messages={preview.messages} manifest={{}} mimeFor={mimeFor}
                  title={preview.title} resolve={preview.resolve} />
      </main>

      <aside className="keep">
        <div>
          <strong>This is only on your device.</strong>
          <span>Nothing has been uploaded and no account exists yet. Close the tab and it
                is gone.</span>
        </div>
        <div className="ways">
          <button onClick={onKeep} disabled={busy}>
            {busy ? 'Waiting…' : 'Keep this archive'}
          </button>
          {/* Someone who already has an archive can arrive here too — most
              often by sharing an export straight out of WhatsApp, which
              reloads the tab and leaves a passkey account locked. Without
              this the only way on was Google, and going back to the gate
              threw the file away. */}
          {onPasskey && (
            <button className="link" onClick={onPasskey} disabled={busy}>
              I already have one
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

/** The drop target on the gate, before anything has been read. */
export function TryIt({ onFile, busy }: { onFile: (f: File) => void; busy: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  return (
    <div className={`tryit${drag ? ' over' : ''}`}
         onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
         onDragLeave={() => setDrag(false)}
         onDrop={(e) => {
           e.preventDefault(); setDrag(false);
           const f = e.dataTransfer.files[0];
           if (f) onFile(f);
         }}>
      <input ref={input} id="try-file" type="file" accept=".zip,application/zip" hidden
             onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
      <button onClick={() => input.current?.click()} disabled={busy}>
        {busy ? 'Reading…' : 'Open an export'}
      </button>
      <p className="fine">or drop a <code>.zip</code> here. It stays on this device —
         no account, nothing uploaded.</p>
    </div>
  );
}


/**
 * Says what happened to the attachments, in one line, before anyone has to ask.
 *
 * "I can't see the pictures" has at least four causes that look identical on
 * screen — the export had none, the names did not match, the format is one no
 * browser will draw, or the file is there and something else broke. This
 * distinguishes them, and the copy button produces a report with counts and
 * file extensions and no message text in it.
 */
function MediaReport({ report: r }: { report: import('../lib/preview').PreviewReport }) {
  const [copied, setCopied] = useState(false);
  const exts = Object.entries(r.extensions)
    .sort((a, b) => b[1] - a[1]).map(([e, n]) => `${n}×.${e}`).join(' ');

  const summary = (() => {
    if (r.filesInZip === 0 && r.omitted > 0)
      return `This export has no photos or videos in it — ${r.omitted} were left out when it was made.`;
    if (r.filesInZip === 0) return 'This export contains no attachments.';
    if (r.matched === 0)
      return `${r.filesInZip} files are in the export, but none match what the messages refer to.`;
    if (r.missing.length > 0)
      return `${r.matched} of ${r.referenced} attachments found. ${r.missing.length} named in the chat are not in the file.`;
    return `${r.matched} attachment${r.matched === 1 ? '' : 's'} found — ${exts}.`;
  })();

  const trouble = r.filesInZip === 0 || r.matched < r.referenced;

  const details = [
    `files in zip: ${r.filesInZip}`,
    `referenced by messages: ${r.referenced}`,
    `matched: ${r.matched}`,
    `omitted markers: ${r.omitted}`,
    `extensions: ${exts || '(none)'}`,
    r.missing.length ? `missing: ${r.missing.join(', ')}` : '',
    r.orphans.length ? `unreferenced: ${r.orphans.join(', ')}` : '',
    r.sample.length ? `sample names: ${r.sample.join(', ')}` : '',
  ].filter(Boolean).join('\n');

  return (
    <div className={`mediareport${trouble ? ' trouble' : ''}`}>
      <span>{summary}</span>
      {r.filesInZip === 0 && r.omitted > 0 && (
        <small>Export it again from WhatsApp and choose <strong>Attach Media</strong>.</small>
      )}
      <button className="link" onClick={() => {
        void navigator.clipboard?.writeText(details).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}>{copied ? 'Copied' : 'Copy details'}</button>
    </div>
  );
}
